import { io, Socket } from 'socket.io-client';

class SocketService {
    private socket: Socket | null = null;
    private isConnecting: boolean = false;

    /**
     * Connect to the socket server with authentication
     * @param token - User authentication token
     */
    connect(token: string): Promise<Socket> {
        return new Promise((resolve, reject) => {
            if (this.socket?.connected) {
                resolve(this.socket);
                return;
            }

            if (this.isConnecting) {
                reject(new Error('Connection already in progress'));
                return;
            }

            this.isConnecting = true;

            try {
                // Connect to the socket server with token as query parameter
                this.socket = io('https://pechu-api.canteradigital.io', {
                    query: {
                        token: token
                    },
                    transports: ['polling'],
                    timeout: 20000,
                    forceNew: true
                });

                this.socket.on('connect', () => {
                    console.log('Socket connected successfully');
                    this.isConnecting = false;
                    resolve(this.socket!);
                });

                this.socket.on('connect_error', (error) => {
                    console.error('Socket connection error:', error);
                    this.isConnecting = false;
                    this.socket = null;
                    reject(error);
                });

                this.socket.on('disconnect', (reason) => {
                    console.log('Socket disconnected:', reason);
                    this.socket = null;
                });

                this.socket.on('error', (error) => {
                    console.error('Socket error:', error);
                });

            } catch (error) {
                this.isConnecting = false;
                reject(error);
            }
        });
    }

    /**
     * Disconnect from the socket server
     */
    disconnect(): void {
        if (this.socket) {
            this.socket.disconnect();
            this.socket = null;
        }
        this.isConnecting = false;
    }

    /**
     * Get the current socket instance
     */
    getSocket(): Socket | null {
        return this.socket;
    }

    /**
     * Check if socket is connected
     */
    isConnected(): boolean {
        return this.socket?.connected || false;
    }

    /**
     * Emit an event to the server
     * @param event - Event name
     * @param data - Data to send
     */
    emit(event: string, data?: any): void {
        if (this.socket?.connected) {
            this.socket.emit(event, data);
        } else {
            console.warn('Socket not connected. Cannot emit event:', event);
        }
    }

    /**
     * Emit an event to the server with acknowledgment callback
     * @param event - Event name
     * @param data - Data to send
     * @param callback - Acknowledgment callback function
     */
    emitWithAck(event: string, data?: any, callback?: (response: any) => void): void {
        if (this.socket?.connected) {
            this.socket.emit(event, data, callback);
        } else {
            console.warn('Socket not connected. Cannot emit event:', event);
            if (callback) {
                callback({ error: 'Socket not connected' });
            }
        }
    }

    /**
     * Emit an event to the server and return a promise for acknowledgment
     * @param event - Event name
     * @param data - Data to send
     * @param timeout - Timeout in milliseconds (default: 5000ms)
     * @returns Promise that resolves with the acknowledgment response
     */
    emitWithPromise(event: string, data?: any, timeout: number = 5000): Promise<any> {
        return new Promise((resolve, reject) => {
            if (!this.socket?.connected) {
                reject(new Error('Socket not connected'));
                return;
            }

            const timeoutId = setTimeout(() => {
                reject(new Error(`Event '${event}' timed out after ${timeout}ms`));
            }, timeout);

            this.socket!.emit(event, data, (response: any) => {
                clearTimeout(timeoutId);
                if (response && response.error) {
                    reject(new Error(response.error));
                } else {
                    resolve(response);
                }
            });
        });
    }

    /**
     * Listen to an event from the server
     * @param event - Event name
     * @param callback - Callback function
     */
    on(event: string, callback: (...args: any[]) => void): void {
        if (this.socket) {
            this.socket.on(event, callback);
        }
    }

    /**
     * Remove event listener
     * @param event - Event name
     * @param callback - Callback function (optional)
     */
    off(event: string, callback?: (...args: any[]) => void): void {
        if (this.socket) {
            if (callback) {
                this.socket.off(event, callback);
            } else {
                this.socket.off(event);
            }
        }
    }
}

// Create a singleton instance
const socketService = new SocketService();

export default socketService; 